import type { FieldSchema } from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';
// `zod/v3`: see the note on `matchValueSchema`.
import type { z as zodNamespace } from 'zod/v3';

import {
  AZURE_RESOURCE_GROUP_PICKER,
  AZURE_RESOURCE_PICKER,
  AZURE_SUBSCRIPTION_PICKER,
} from './field-names';
import { matchValueSchema } from './match';
import { AzureResourceGroupPickerComponent } from './resource-group-picker';
import type { ResourceGroupPickerUiOptions } from './resource-group-query';
import { AzureResourcePickerComponent } from './resource-picker';
import type { ResourcePickerUiOptions } from './resource-query';
import { AzureSubscriptionPickerComponent } from './subscription-picker';
import type { SubscriptionPickerUiOptions } from './subscription-query';
import type {
  AzureResource,
  AzureResourceGroup,
  AzureSubscription,
} from './types';
import {
  resourceGroupValidator,
  resourceValidator,
  subscriptionValidator,
} from './validation';

/**
 * The three `ui:field` names, verbatim.
 *
 * @remarks
 * These strings are an EXTERNAL contract. NS Cloud authors templates in another
 * repository that refers to them dozens of times, so renaming one breaks templates
 * this repository cannot see. `azurePickerFieldNames` exists so a test can read
 * the registered names back instead of trusting a comment.
 */
export const AZURE_PICKER_FIELD_NAMES = [
  AZURE_SUBSCRIPTION_PICKER,
  AZURE_RESOURCE_GROUP_PICKER,
  AZURE_RESOURCE_PICKER,
] as const;

/**
 * The `ui:options` shape of the subscription picker.
 *
 * @remarks
 * Named and exported rather than written inline so `fields.test-d.ts` can reach
 * the DERIVED type with `z.output<ReturnType<typeof subscriptionUiOptions>>`
 * and compare it against {@link SubscriptionPickerUiOptions}. There is no other
 * route to it: `FieldSchema.TProps` reports the DECLARED type, so a check
 * against it can never go red.
 *
 * `environment` and `state` stay plain string lists. Their vocabulary is
 * closed and `buildSubscriptionQuery` checks it directly, so neither goes
 * through `resolveMatch` and neither is a {@link MatchValue}.
 */
export function subscriptionUiOptions(z: typeof zodNamespace) {
  return z.object({
    filter: z
      .object({
        environment: z.array(z.string()).optional(),
        state: z.array(z.string()).optional(),
        tags: z.record(z.string(), matchValueSchema(z)).optional(),
      })
      .optional(),
  });
}

export const azureSubscriptionPickerSchema: FieldSchema<
  AzureSubscription,
  SubscriptionPickerUiOptions
> = makeFieldSchema({
  output: z => z.custom<AzureSubscription>(),
  uiOptions: subscriptionUiOptions,
});

/**
 * The `ui:options` shape of the resource-group picker.
 *
 * @remarks
 * Exported for the same reason as {@link subscriptionUiOptions}. `filter` and
 * `exclude` share one shape, because a key that may narrow the list may also
 * strike rows out of it.
 */
export function resourceGroupUiOptions(z: typeof zodNamespace) {
  const tagFilter = z
    .object({
      location: matchValueSchema(z).optional(),
      tags: z.record(z.string(), matchValueSchema(z)).optional(),
    })
    .optional();
  return z.object({
    exclude: tagFilter,
    filter: tagFilter,
    subscriptionRef: z.string().optional(),
  });
}

export const azureResourceGroupPickerSchema: FieldSchema<
  AzureResourceGroup,
  ResourceGroupPickerUiOptions
> = makeFieldSchema({
  output: z => z.custom<AzureResourceGroup>(),
  uiOptions: resourceGroupUiOptions,
});

export const azureResourcePickerSchema: FieldSchema<
  AzureResource,
  ResourcePickerUiOptions
> = makeFieldSchema({
  output: z => z.custom<AzureResource>(),
  uiOptions: z =>
    z.object({
      resourceGroupRef: z.string().optional(),
      resourceType: z.string().optional(),
      subscriptionRef: z.string().optional(),
    }),
});

/**
 * What `createFormField` puts in the object it returns.
 *
 * @remarks
 * `FormField` is deliberately opaque: its public type is `{ $$type }` and
 * nothing else, so the registered name is unreachable through it. Measured
 * 2026-09-17, the returned object carries `component`, `name`, `schema`,
 * `$$type` and `version`, and `name` holds the string passed in.
 *
 * This shape exists so the cast lives in ONE place with its reason, instead of
 * once per assertion. The alternative — asserting on the argument before it
 * goes in — would test the object we just wrote down, and could no longer fail
 * if `createFormField` ever passed the name through differently.
 */
type RegisteredFormField = FormField & {
  readonly component: unknown;
  readonly name: string;
  readonly schema: unknown;
  readonly validation: unknown;
};
function isRegisteredFormField(field: FormField): field is RegisteredFormField {
  return (
    'name' in field &&
    typeof field.name === 'string' &&
    'component' in field &&
    'schema' in field &&
    'validation' in field
  );
}

/** Reads back the `ui:field` name a picker is registered under. */
export function registeredFieldOf(field: FormField): RegisteredFormField {
  if (!isRegisteredFormField(field)) {
    throw new TypeError(
      'A form field lacks the registration details createFormField stores.',
    );
  }
  return field;
}

export const AzureSubscriptionPicker: FormField = createFormField({
  component: AzureSubscriptionPickerComponent,
  name: AZURE_SUBSCRIPTION_PICKER,
  schema: azureSubscriptionPickerSchema,
  validation: subscriptionValidator,
});

export const AzureResourceGroupPicker: FormField = createFormField({
  component: AzureResourceGroupPickerComponent,
  name: AZURE_RESOURCE_GROUP_PICKER,
  schema: azureResourceGroupPickerSchema,
  validation: resourceGroupValidator,
});

export const AzureResourcePicker: FormField = createFormField({
  component: AzureResourcePickerComponent,
  name: AZURE_RESOURCE_PICKER,
  schema: azureResourcePickerSchema,
  validation: resourceValidator,
});
